import { DialogDescription } from '@radix-ui/react-dialog'
import {
  CheckCircle,
  Clock,
  Info,
  PartyPopper,
  Play,
  RotateCw,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useHeadManager } from '@/lib/headManagerProvider'
import { api } from '@/services/axios'

interface Premio {
  id: number
  premio: string
  numeroSorteado: number | null
  statusSorteio: string
}

interface Ganhador extends Premio {
  nomeComprador: string
  whatsappComprador: string
  emailComprador: string
}

export function Drawer() {
  const { setTitle } = useHeadManager()
  const { rifaId } = useParams<{ rifaId: string }>()
  const [loading, setLoading] = useState(true)
  const [premios, setPremios] = useState<Premio[]>([])
  const [ganhadores, setGanhadores] = useState<Ganhador[]>([])
  const [premioSendoSorteado, setPremioSendoSorteado] = useState<number | null>(
    null,
  )
  const [numeroAtual, setNumeroAtual] = useState(0)
  const [mostrarCelebracao, setMostrarCelebracao] = useState(false)
  const [mostrarModal, setMostrarModal] = useState(false)
  const [ganhadorSelecionado, setGanhadorSelecionado] =
    useState<Ganhador | null>(null)
  const [historicoSorteio, setHistoricoSorteio] = useState<Premio[]>([])
  const [maxNumber, setMaxNumber] = useState(0)

  useEffect(() => {
    setTitle('Sortear')
  }, [setTitle])

  useEffect(() => {
    if (rifaId) {
      carregarDados()
    }
  }, [rifaId])

  async function carregarDados() {
    try {
      setLoading(true)
      await Promise.all([
        carregarPremios(),
        carregarGanhadores(),
        carregarMaxNumber(),
      ])
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    } finally {
      setLoading(false)
    }
  }

  async function carregarMaxNumber() {
    const { data } = await api.get(`Campanha/GetById/${rifaId}`)
    setMaxNumber(data.campanha.titulo_Quantidade)
  }

  async function carregarPremios() {
    const { data } = await api.get(
      `CampanhaPremio/GetResultadoSorteio/${rifaId}`,
    )
    setPremios(
      data.map((item: any) => ({
        id: item.id,
        premio: item.premio || 'Prêmio',
        numeroSorteado: item.numeroSorteado > 0 ? item.numeroSorteado : null,
        statusSorteio: item.statusSorteio,
      })),
    )

    const premiosSorteados = data.filter((item: any) => item.numeroSorteado > 0)
    setHistoricoSorteio(premiosSorteados)
  }

  async function carregarGanhadores() {
    const { data } = await api.get(
      `CampanhaPremio/GetResultadoSorteio/${rifaId}`,
    )
    setGanhadores(
      data.map((item: any) => ({
        ...item,
        numeroSorteado: item.numeroSorteado > 0 ? item.numeroSorteado : null,
      })),
    )
  }

  const animarNumero = (onComplete: (numero: number) => void) => {
    let counter = 0
    const interval = setInterval(() => {
      setNumeroAtual(Math.floor(Math.random() * maxNumber) + 1)
      counter++
      if (counter > 100) {
        clearInterval(interval)
        const numeroSorteado = Math.floor(Math.random() * maxNumber) + 1
        // const numeroSorteado = 25 - variavel usada nos testes
        setNumeroAtual(numeroSorteado)
        onComplete(numeroSorteado)
      }
    }, 50)
  }

  async function atualizarPremio(
    id: number,
    numero: number | null,
    status: Premio['statusSorteio'],
  ) {
    let userId: number | null = null

    if (status !== 'Não Sorteado') {
      const { data } = await api.get<number>(
        `CampanhaPremio/GetUserPurchasedNumber/${numero}`,
      )
      userId = data
    }

    if (status === 'Não Sorteado') {
      await api.put(`CampanhaPremio/UpdateFlagSorteado/${rifaId}`, {
        sorteado: false,
      })
    }

    await api.put(
      `CampanhaPremio/UpdatePremio/${id}`,
      {
        numeroSorteado: numero,
        novoStatus: status,
        userId: status === 'Não Sorteado' ? null : userId,
      },
      {
        headers: { 'Content-Type': 'application/json' },
      },
    )

    await carregarGanhadores()
  }

  async function verificarTodosSorteados() {
    const { data: premios } = await api.get<Premio[]>(
      `CampanhaPremio/GetResultadoSorteio/${rifaId}`,
    )
    if (premios.every((p) => p.statusSorteio === 'Sorteado')) {
      try {
        await api.put(`CampanhaPremio/UpdateFlagSorteado/${rifaId}`, {
          sorteado: true,
        })
      } catch (error) {
        console.error('Erro ao atualizar flag sorteado:', error)
      }
    }
  }

  function sortearPremio(index: number) {
    if (premioSendoSorteado !== null || premios[index].numeroSorteado !== null)
      return

    setPremioSendoSorteado(index)
    setMostrarCelebracao(false)

    animarNumero(async (numero) => {
      const novosPremios = [...premios]
      const premioSorteado = {
        ...novosPremios[index],
        numeroSorteado: numero,
        statusSorteio: 'Sorteado',
      }

      novosPremios[index] = premioSorteado
      setPremios(novosPremios)
      setHistoricoSorteio((prev) => [...prev, premioSorteado])

      await atualizarPremio(novosPremios[index].id, numero, 'Sorteado')
      await verificarTodosSorteados()

      setPremioSendoSorteado(null)
      setMostrarCelebracao(true)
      setTimeout(() => setMostrarCelebracao(false), 3000)
    })
  }

  function sortearTodos() {
    if (premioSendoSorteado !== null || todosSorteados) return

    const premiosAtualizados = [...premios]
    setHistoricoSorteio([])

    const sortearProximo = async (index: number) => {
      if (index >= premiosAtualizados.length) {
        setPremioSendoSorteado(null)
        await verificarTodosSorteados()
        setPremios(premiosAtualizados)
        return
      }

      if (premiosAtualizados[index].numeroSorteado !== null) {
        sortearProximo(index + 1)
        return
      }

      setPremioSendoSorteado(index)
      setMostrarCelebracao(false)

      animarNumero(async (numero) => {
        const premioSorteado = {
          ...premiosAtualizados[index],
          numeroSorteado: numero,
          statusSorteio: 'Sorteado',
        }

        premiosAtualizados[index] = premioSorteado
        setHistoricoSorteio((prev) => [...prev, premioSorteado])

        await atualizarPremio(premiosAtualizados[index].id, numero, 'Sorteado')

        setMostrarCelebracao(true)
        setTimeout(() => {
          setMostrarCelebracao(false)
          sortearProximo(index + 1)
        }, 2000)
      })
    }

    sortearProximo(0)
  }

  async function reiniciarSorteio() {
    const premiosResetados = premios.map((p) => ({
      ...p,
      numeroSorteado: null,
      statusSorteio: 'Não Sorteado',
    }))

    setPremios(premiosResetados)
    setHistoricoSorteio([])
    setNumeroAtual(0)
    setMostrarCelebracao(false)
    setPremioSendoSorteado(null)

    await Promise.all(
      premiosResetados.map((p) => atualizarPremio(p.id, null, 'Não Sorteado')),
    )
    await carregarGanhadores()
  }

  const todosSorteados =
    premios.length > 0 && premios.every((p) => p.numeroSorteado !== null)

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
        <Skeleton className="h-64 w-full rounded-2xl" />
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
      {/* Área de Controle do Sorteio */}
      <Card className="rounded-2xl shadow-md">
        <CardContent className="space-y-4 px-6 py-8 text-center">
          <div className="min-h-16">
            {premioSendoSorteado !== null ? (
              <div className="animate-pulse">
                <h2 className="text-primary text-4xl font-extrabold">
                  {numeroAtual}
                </h2>
                <p className="text-muted-foreground text-lg">
                  Sorteando: {premios[premioSendoSorteado]?.premio}
                </p>
              </div>
            ) : (
              <>
                <h2 className="text-primary text-4xl font-extrabold">
                  {todosSorteados ? 'Sorteio Concluído!' : 'Rifaqui - Sorteio'}
                </h2>
                <p className="text-muted-foreground text-lg">
                  {todosSorteados
                    ? 'Todos os prêmios foram sorteados'
                    : 'Clique para iniciar o sorteio'}
                </p>
              </>
            )}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button
              variant="default"
              size="lg"
              onClick={sortearTodos}
              disabled={
                premioSendoSorteado !== null ||
                todosSorteados ||
                premios.length === 0
              }
              className="w-full sm:w-auto"
            >
              Sortear Todos
            </Button>

            {todosSorteados && (
              <Button
                variant="outline"
                size="lg"
                onClick={reiniciarSorteio}
                className="w-full sm:w-auto"
              >
                <RotateCw className="mr-2 h-4 w-4" />
                Refazer Sorteio
              </Button>
            )}
          </div>

          {mostrarCelebracao && (
            <div className="mt-4 flex justify-center">
              <PartyPopper className="text-primary h-16 w-16 animate-bounce" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Histórico do Sorteio em Tempo Real */}
      {(historicoSorteio.length > 0 || premioSendoSorteado !== null) && (
        <div className="space-y-4">
          <div className="flex flex-wrap justify-center gap-4">
            {historicoSorteio.map(({ premio, numeroSorteado }, index) => (
              <div
                key={`historico-${index}`}
                className="bg-muted/100 flex items-center space-x-2 rounded-lg px-4 py-2 shadow-md"
              >
                <PartyPopper className="text-primary h-6 w-6" />
                <span className="font-semibold">{premio}</span>
                <span className="text-primary font-bold">
                  #{numeroSorteado}
                </span>
              </div>
            ))}

            {premioSendoSorteado !== null && (
              <div className="bg-muted/100 flex animate-pulse items-center space-x-2 rounded-lg px-4 py-2 shadow-md">
                <PartyPopper className="text-primary h-6 w-6 animate-bounce" />
                <span className="font-semibold">
                  {premios[premioSendoSorteado]?.premio}
                </span>
                <span className="text-primary font-bold">#{numeroAtual}</span>
              </div>
            )}
          </div>
          <Separator />
        </div>
      )}

      {/* Tabela de Prêmios */}
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead className="w-[40%]">Prêmio</TableHead>
              <TableHead className="text-center">Número Sorteado</TableHead>
              <TableHead className="text-center">Situação</TableHead>
              <TableHead className="text-center">Ação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {premios.length > 0 ? (
              premios.map(
                ({ id, premio, numeroSorteado, statusSorteio }, index) => {
                  const ganhador = ganhadores.find((g) => g.id === id)
                  return (
                    <TableRow key={id}>
                      <TableCell className="font-medium">{premio}</TableCell>
                      <TableCell className="text-center">
                        {numeroSorteado ? (
                          <span className="text-primary font-bold">
                            #{numeroSorteado}
                          </span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center">
                          {statusSorteio === 'Sorteado' ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : (
                            <Clock className="h-5 w-5 text-yellow-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-2">
                          {ganhador ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setGanhadorSelecionado(ganhador)
                                setMostrarModal(true)
                              }}
                              title="Informações do Vencedor"
                            >
                              <Info className="h-4 w-4" />
                            </Button>
                          ) : (
                            <span>-</span>
                          )}

                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={
                              premioSendoSorteado !== null ||
                              numeroSorteado !== null
                            }
                            onClick={() => sortearPremio(index)}
                            title="Sortear Prêmio"
                            className="p-0"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                },
              )
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  Nenhum prêmio disponível para sorteio
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modal do Ganhador */}
      <Dialog open={mostrarModal} onOpenChange={setMostrarModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Detalhes do Ganhador</DialogTitle>
            <DialogDescription>
              Informações para que você entre em contato com o vencedor.
            </DialogDescription>
          </DialogHeader>
          {ganhadorSelecionado && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="col-span-1 text-sm font-medium">Prêmio:</p>
                <p className="col-span-3 text-sm font-semibold">
                  {ganhadorSelecionado.premio}
                </p>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="col-span-1 text-sm font-medium">Número:</p>
                <p className="col-span-3 text-sm font-semibold">
                  #{ganhadorSelecionado.numeroSorteado}
                </p>
              </div>
              <Separator />
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="col-span-1 text-sm font-medium">Nome:</p>
                <p className="col-span-3 text-sm">
                  {ganhadorSelecionado.nomeComprador}
                </p>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="col-span-1 text-sm font-medium">Telefone:</p>
                <p className="col-span-3 text-sm">
                  {ganhadorSelecionado.whatsappComprador}
                </p>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="col-span-1 text-sm font-medium">Email:</p>
                <p className="col-span-3 text-sm">
                  {ganhadorSelecionado.emailComprador}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
